import { Core } from "@faire/design-tokens";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate-deprecated/Button";
import { LocationDynamic } from "@faire/web/slate/icons/Location";
import DropdownList from "@faire/web/slate/Input/DropdownList";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, type JSX } from "react";
import styled from "styled-components";

export interface IAddressOption {
  id: string;
  name?: string;
  location?: string;
  google_place_types?: Array<string>;
}

interface IProps {
  children?: React.ReactNode | undefined;
  className?: string;
  onSelect: (selectedOption: IAddressOption) => void;
  options: IAddressOption[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  showCannotFindOption?: boolean;
  onCannotFindClick: () => void;
}

export const CustomAddressDropdown: FC<IProps> = ({
  className,
  children,
  open,
  onOpen,
  onClose,
  options,
  onCannotFindClick,
  showCannotFindOption,
  ...props
}) => {
  const slicedOptions = (options ?? []).slice(0, 4);
  const customOptions = showCannotFindOption
    ? [{ id: CANT_FIND_OPTION_ID }]
    : [];
  const dropdownOptions = [...slicedOptions, ...customOptions];

  const onSelect = (value: string) => {
    if (value === CANT_FIND_OPTION_ID) {
      onCannotFindClick();
      return;
    }
    const selectedOption = options.find((option) => option.id === value);
    if (selectedOption) {
      props.onSelect(selectedOption);
    }
  };

  const renderOption = (
    option: IAddressOption,
    onSelect: React.MouseEventHandler<HTMLElement>,
    onMouseOver: React.MouseEventHandler<HTMLElement>,
    selectedValue?: string
  ) => (
    <Option
      key={option.id}
      {...option}
      onSelect={onSelect}
      onMouseOver={onMouseOver}
      selectedValue={selectedValue}
      onCannotFindClick={onCannotFindClick}
    />
  );

  return (
    <DropdownList
      classNameDropdown={className}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      getLabel={getLabel}
      getValue={getValue}
      options={dropdownOptions}
      renderOption={renderOption}
      onSelect={onSelect}
      appendage={Appendage}
    >
      {children}
    </DropdownList>
  );
};

interface IOption extends IAddressOption {
  onSelect: React.MouseEventHandler<HTMLElement>;
  onMouseOver: React.MouseEventHandler<HTMLElement>;
  selectedValue?: string;
  onCannotFindClick: () => void;
}

const Option: FC<IOption> = ({
  id,
  name,
  location,
  onSelect,
  onMouseOver,
  selectedValue,
  onCannotFindClick,
}) =>
  id !== CANT_FIND_OPTION_ID ? (
    <DropdownButton
      type="button"
      $selected={selectedValue === id}
      onClick={onSelect}
      onMouseOver={onMouseOver}
      key={id}
    >
      <Spacer size="1x" />
      <LocationDynamic height="12px" solid color={Core.icon.subdued} />
      <Spacer size="1x" />
      <Typography variant="labelSansRegular">{name}</Typography>
      <Spacer size="1x" />
      <EndingEllipsis>{location}</EndingEllipsis>
    </DropdownButton>
  ) : (
    <FooterButton
      key="footer-button"
      type="button"
      onMouseOver={onMouseOver}
      $selected={selectedValue === id}
      onClick={onCannotFindClick}
    >
      <Typography variant="labelSansRegular">
        <StrictLocalMsg
          id="+DwZ4E"
          defaultMessage="Can't find your address? <LinkFormatting>Click Here</LinkFormatting>"
          values={{
            LinkFormatting: CantFindAddressLink,
          }}
          description={{
            text: "Link to the address lookup page",
          }}
        />
      </Typography>
    </FooterButton>
  );

const getValue = ({ id }: IAddressOption) => id;

const getLabel = ({ name, location }: IAddressOption) => `${name} ${location}`;

const CantFindAddressLink = (chunks: JSX.Element) => (
  <Button
    type="button"
    variant="tertiary"
    data-test-id="can-not-find-address-button"
  >
    {chunks}
  </Button>
);

const GoogleMaps = styled.img`
  width: 75px;
  height: auto;
  position: relative;
  top: 4px;
`;

const PoweredBy = styled(Row).attrs({
  align: "center",
})`
  width: 100%;
  height: 30px;
  padding: 0 14px;
`;

const Appendage = (
  <PoweredBy>
    <Typography variant="labelSansRegular">
      <StrictLocalMsg
        id="426rKy"
        defaultMessage="Powered By {logo}"
        description={{
          text: "Powered by [Google logo]",
        }}
        values={DEPRECATED_backwardsCompatibleValues({
          logo: (
            <GoogleMaps src="https://cdn.faire.com/fastly/f98d8a1a357fa91b88ee5b67c51ed3de79f4ae19cb8f8b6f1a7b45af18a61691.png" />
          ),
        })}
      />
    </Typography>
  </PoweredBy>
);

const CANT_FIND_OPTION_ID = "cantfind";

const EndingEllipsis = styled(Typography).attrs({
  color: Core.text.subdued,
  variant: "labelSansRegular",
})`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownButton = styled(Row).attrs({ align: "center" })<{
  $selected?: boolean;
}>`
  cursor: pointer;
  width: 100%;
  height: 40px;
  background-color: ${(props) =>
    props.$selected ? Core.surface.subdued : "unset"};
  outline: 0;
  border: 0;
  overflow: hidden;
  white-space: nowrap;
`;

const FooterButton = styled(DropdownButton)`
  padding: 0 16px;
  height: 42px;
  border-top: 1px solid ${Core.surface.subdued};
  border-bottom: 1px solid ${Core.surface.subdued};
`;
